import React, { useState, useEffect } from "react";
import {
  Button,
  Modal,
  message,
  Collapse,
  Card,
  Typography,
  Table,
  Tag,
  Space,
  Alert,
  Divider,
  List,
  InputNumber,
} from "antd";
import {
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  UserOutlined,
  EnvironmentOutlined,
  LockOutlined,
  WarningOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import axios from "axios";
import {
  baseUrl,
  GET_GEO_ALL,
  HEADERS_EXPORT,
  HEADERS_EXPORT_BEN,
  POST_ADD_ADDRESS,
  PUT_ADD_ADDRESS,
} from "../../../API/apiUrls";
import CurrencyFormat from "../../../../hook/CurrencyFormat";
import AddressFormModal from "./AddressFromModal";
import CourtSearchModal from "./CourtSearchModal";

const fullName = (p) => [p?.SNAM, p?.NAME1, p?.NAME2].filter(Boolean).join(" ");

const SelectCourtArea = ({ open, close, dataLoan, courtFee, funcUpdate }) => {
  const [
    currencyFormat,
    currencyFormatComma,
    currencyFormatPoint,
    currencyFormatNoPoint,
  ] = CurrencyFormat();

  const [loadData, setLoading] = useState(false);
  const [customerAddr, setCustomerAddr] = useState([]);
  const [geoRawData, setGeoRawData] = useState(null);
  const [activeKeys, setActiveKeys] = useState([]);
  const [editableCourtDetails, setEditableCourtDetails] = useState([]);

  // ✅ แก้ไขโครงสร้างข้อมูลศาล - เก็บแยกตาม customerIndex
  const [selectedCourtData, setSelectedCourtData] = useState({});

  // รูปแบบ: { customerIndex: { courtData, applyToAll } }
  // Modal states - Address
  const [addressModalVisible, setAddressModalVisible] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const [currentPerson, setCurrentPerson] = useState(null);

  // Modal states - Court
  const [courtModalVisible, setCourtModalVisible] = useState(false);
  const [currentCourtCustomerIndex, setCurrentCourtCustomerIndex] =
    useState(null);
  const [editingCourt, setEditingCourt] = useState(null);

  // Modal states - success

  const handleCancel = () => {
    close(false);
  };

  // ==================== HELPER FUNCTIONS ====================

  // ✅ ฟังก์ชันเช็คว่าที่อยู่เป็นที่อยู่เดิมหรือไม่
  const isOldAddress = (address) => {
    // ถ้ามี key ที่ไม่ได้เริ่มด้วย "addr-" แสดงว่าเป็นที่อยู่เดิม
    // หรือถ้าไม่มี key เลย (มาจาก database เดิม)
    return !address.key || !address.key.startsWith("addr-");
  };

  // ✅ คำนวณค่าส่งหมายรวม
  const calculateTotalCourtFee = (finalData) => {
    let total = 0;
    const processedEntries = new Set();
    const courtDetailsMap = new Map(); // ✅ ใช้ Map เพื่อ group ตามศาล

    const normalize = (text) => {
      if (!text) return "";
      return text
        .replace(
          /^(จ\.|จังหวัด|อ\.|อำเภอ|เขต|ต\.|ตำบล|แขวง|หมู่|ม\.บ้าน|หมู่บ้าน\.)\s*/gi,
          ""
        )
        .toLowerCase()
        .replace(/\s+/g, "")
        .replace(/[^\wก-๙]/g, "");
    };

    finalData?.forEach((customer) => {
      if (customer.COURT && customer.COURT.id) {
        let address = customer.ADDRESS?.find((addr) => addr.ADDRNO === 4);
        if (!address) {
          address = customer.ADDRESS?.find((addr) => addr.ADDRNO === 1);
        }
        if (!address && customer.ADDRESS?.length > 0) {
          address = customer.ADDRESS[0];
        }

        if (address) {
          const uniqueKey = JSON.stringify({
            courtId: customer.COURT.id,
            addr1: normalize(address.ADDR1),
            addr2: normalize(address.ADDR2),
            tumb: normalize(address.TUMB),
            aumpdes: normalize(address.AUMPDES),
            provdes: normalize(address.PROVDES),
            zip: normalize(address.ZIP),
          });

          if (!processedEntries.has(uniqueKey)) {
            const fee = parseFloat(customer.COURT.amtsentnotice) || 0;
            total += fee;
            processedEntries.add(uniqueKey);

            const courtKey = customer.COURT.court_name;

            // ✅ ถ้ายังไม่มีศาลนี้ใน Map ให้เพิ่มเข้าไป
            if (!courtDetailsMap.has(courtKey)) {
              courtDetailsMap.set(courtKey, {
                courtName: customer.COURT.court_name,
                province: customer.COURT.prov_name,
                remark: customer.COURT.remark, // ✅ เพิ่ม remark
                locations: [], // ✅ เก็บหลายพื้นที่ของศาลเดียวกัน
                totalFee: 0,
              });
            }

            // ✅ เพิ่มพื้นที่เข้าไปในศาลนั้น
            const courtData = courtDetailsMap.get(courtKey);
            courtData.locations.push({
              district: address.AUMPDES,
              subDistrict: address.TUMB || customer.COURT.tambon_name,
              address: address.ADDR1,
              fee: fee,
            });
            courtData.totalFee += fee;
          }
        }
      }
    });

    // ✅ แปลง Map เป็น Array
    const courtDetails = Array.from(courtDetailsMap.values());

    return { total, courtDetails };
  };

  // ✅ Validate ข้อมูลก่อนยืนยัน
  const validateBeforeSubmit = () => {
    const issues = [];

    customerAddr.forEach((person) => {
      const personIssues = [];
      const personName = fullName(person);

      // เช็คว่ามีที่อยู่ทะเบียนราษหรือไม่
      const hasRegistrationAddress = person.ADDRESS?.some(
        (addr) => addr.ADDRNO === 4 || addr.ADDRNO
      );
      if (!hasRegistrationAddress) {
        personIssues.push({
          type: "address",
          message: "ยังไม่มีที่อยู่ทะเบียนราษ ",
        });
      }

      // เช็คว่ามีศาลหรือไม่
      const hasCourt = !!selectedCourtData[person.customerIndex];
      if (!hasCourt) {
        personIssues.push({
          type: "court",
          message: "ยังไม่ได้เพิ่มศาล",
        });
      }

      if (personIssues.length > 0) {
        issues.push({
          personName,
          personIndex: person.customerIndex,
          issues: personIssues,
        });
      }
    });

    return issues;
  };

  // ✅ รวมข้อมูลศาลเข้ากับ customerAddr เพื่อเตรียม POST
  const prepareDataForSubmit = () => {
    return customerAddr.map((person) => {
      const courtInfo = selectedCourtData[person.customerIndex];

      return {
        ...person,
        COURT: courtInfo
          ? {
              ...courtInfo.courtData,
              applyToAll: courtInfo.applyToAll,
              sourceCustomerIndex: courtInfo.sourceCustomerIndex,
            }
          : null,
      };
    });
  };

  // Load Geo Data
  useEffect(() => {
    const loadGeoData = async () => {
      setLoading(true);
      try {
        const res = await axios.get(GET_GEO_ALL, {
          headers: HEADERS_EXPORT_BEN,
        });
        setGeoRawData(res.data);
        console.log("Geo Data:", res.data);
      } catch (error) {
        message.error(`ไม่พบข้อมูล: ${error.message}`);
      } finally {
        setLoading(false);
      }
    };
    if (open) {
      loadGeoData();
    }
  }, [open]);

  // Merge Customer & Guarantors
  useEffect(() => {
    if (dataLoan) {
      const allPersons = mergeCustomerAndGuarantors(dataLoan);
      console.log("All Persons:", allPersons);
      setCustomerAddr(allPersons);
    }
  }, [dataLoan]);

  const mergeCustomerAndGuarantors = (loanData) => {
    const result = [];

    if (loanData.CUSTOMER) {
      result.push({
        ...loanData.CUSTOMER,
        ROLE: "CUSTOMER",
        GARNO: 0,
        RELATN: "ผู้กู้",
        key: `customer-${loanData.CUSTOMER.CUSCOD || 0}`,
        customerIndex: 0,
        ADDRESS: loanData.CUSTOMER.ADDRESS || [],
      });
    }

    if (loanData.GUARANTORS && Array.isArray(loanData.GUARANTORS)) {
      loanData.GUARANTORS.forEach((guarantor, idx) => {
        result.push({
          ...guarantor,
          ROLE: "GUARANTOR",
          key: `guarantor-${guarantor.GARNO || idx}`,
          customerIndex: idx + 1,
          ADDRESS: guarantor.ADDRESS || [],
        });
      });
    }

    return result;
  };

  // Toggle Collapse
  const toggleItem = (key) => {
    setActiveKeys((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  // ==================== NORMALIZE TEXT ====================
  const normalizeText = (text) => {
    if (!text) return "";
    return text
      .replace(/^(จ\.|จังหวัด|อ\.|อำเภอ|เขต|ต\.|ตำบล|แขวง)\s*/g, "")
      .trim()
      .replace(/์/g, "")
      .replace(/\s+/g, "")
      .toLowerCase();
  };

  // ==================== FIND SEARCH ADDRESS ====================
  const findSearchAddress = (personIndex) => {
    if (!geoRawData) return null;

    const person = customerAddr[personIndex];
    if (!person) return null;

    const allAddresses = person.ADDRESS || [];

    let searchAddr = allAddresses.find((addr) => addr.ADDRNO === 4);

    if (!searchAddr) {
      searchAddr = allAddresses.find((addr) => addr.ADDRTYPE === 4);
    }

    if (!searchAddr) {
      searchAddr = allAddresses.find((addr) => addr.ADDRTYPE === 1);
    }

    if (!searchAddr && allAddresses.length > 0) {
      searchAddr = allAddresses[0];
    }

    if (!searchAddr) return null;

    // Map ชื่อเป็น code
    let provCode = null;
    let distCode = null;
    let subDistCode = null;

    if (searchAddr.PROVDES && geoRawData.province) {
      const normalizedProv = normalizeText(searchAddr.PROVDES);
      const foundProv = geoRawData.province.find((prov) =>
        normalizeText(prov.provDesc).includes(normalizedProv)
      );
      if (foundProv) {
        provCode = foundProv.provCode;
      }
    }

    if (!provCode && searchAddr.PROVINCE) {
      provCode = searchAddr.PROVINCE;
    }

    if (searchAddr.AUMPDES && provCode && geoRawData.district) {
      const normalizedDist = normalizeText(searchAddr.AUMPDES);
      const foundDist = geoRawData.district.find(
        (dist) =>
          dist.provCode === provCode.toString() &&
          normalizeText(dist.distDesc).includes(normalizedDist)
      );
      if (foundDist) {
        distCode = foundDist.distCode;
      }
    }

    if (!distCode && searchAddr.AMPHUR) {
      distCode = searchAddr.AMPHUR;
    }

    if (searchAddr.TUMB && provCode && distCode && geoRawData.subDistrict) {
      const normalizedSubDist = normalizeText(searchAddr.TUMB);
      const foundSubDist = geoRawData.subDistrict.find(
        (sub) =>
          sub.provCode === provCode.toString() &&
          sub.distCode === distCode &&
          normalizeText(sub.subDistDesc).includes(normalizedSubDist)
      );
      if (foundSubDist) {
        subDistCode = foundSubDist.subDistCode;
      }
    }

    if (!subDistCode && searchAddr.TUMBON) {
      subDistCode = searchAddr.TUMBON;
    }

    return {
      ...searchAddr,
      PROVINCE: provCode,
      AMPHUR: distCode,
      TUMBON: subDistCode,
    };
  };

  // ==================== COURT MANAGEMENT ====================
  // ✅ เปิด Modal เลือกศาล (เพิ่มใหม่)
  const handleOpenCourtModal = (personIndex) => {
    // เช็คว่ามีศาลอยู่แล้วหรือไม่
    if (selectedCourtData[personIndex]) {
      Modal.warning({
        title: "ไม่สามารถเพิ่มศาลได้",
        content:
          "มีศาลอยู่แล้ว 1 แห่ง กรุณาลบศาลเดิมก่อนเพิ่มใหม่ หรือคลิก 'แก้ไข' เพื่อเปลี่ยนศาล",
        okText: "ตกลง",
      });
      return;
    }

    const searchAddr = findSearchAddress(personIndex);

    if (!searchAddr) {
      message.warning("กรุณาเพิ่มที่อยู่ก่อนเลือกศาล");
      return;
    }

    setCurrentCourtCustomerIndex(personIndex);
    setEditingCourt(null); // Reset editing state
    setCourtModalVisible(true);
  };

  // ✅ เปิด Modal แก้ไขศาล (ใหม่)
  const handleEditCourt = (personIndex) => {
    const courtInfo = selectedCourtData[personIndex];
    if (!courtInfo) return;

    const searchAddr = findSearchAddress(personIndex);
    if (!searchAddr) {
      message.warning("กรุณาเพิ่มที่อยู่ก่อนแก้ไขศาล");
      return;
    }

    setCurrentCourtCustomerIndex(personIndex);
    setEditingCourt(courtInfo); // Set editing state
    setCourtModalVisible(true);
  };

  // ✅ เลือกศาล (รองรับทั้งเพิ่มใหม่และแก้ไข)
  const handleSelectCourt = ({ court, customerIndex, applyToAll }) => {
    console.log(
      "Selected court:",
      court,
      "for customer:",
      customerIndex,
      "applyToAll:",
      applyToAll,
      "editing:",
      !!editingCourt
    );

    const newCourt = {
      STATUS: "PENDING",
      amtsentnotice: court.amtsentnotice,
      court_name: court.court_name,
      id: court.id,
      moo: court.moo,
      prov_name: court.prov_name,
      remark: court.remark,
      tambon_name: court.tambon_name,
      createdBy: customerIndex,
      createdAt: editingCourt
        ? editingCourt.courtData.createdAt
        : new Date().toISOString(),
      updatedAt: editingCourt ? new Date().toISOString() : null,
    };

    if (applyToAll) {
      // นำไปใช้กับทุก customer
      const newSelectedCourtData = {};
      customerAddr.forEach((person) => {
        newSelectedCourtData[person.customerIndex] = {
          courtData: newCourt,
          applyToAll: true,
          sourceCustomerIndex: customerIndex,
        };
      });
      setSelectedCourtData(newSelectedCourtData);
      message.success(
        editingCourt
          ? `แก้ไขศาล: ${court.court_name} สำหรับทุกคน`
          : `เพิ่มศาล: ${court.court_name} สำหรับทุกคน`
      );
    } else {
      // ใช้เฉพาะ customer ที่เลือก
      setSelectedCourtData((prev) => ({
        ...prev,
        [customerIndex]: {
          courtData: newCourt,
          applyToAll: false,
          sourceCustomerIndex: customerIndex,
        },
      }));
      const customerName = fullName(customerAddr[customerIndex]);
      message.success(
        editingCourt
          ? `แก้ไขศาล: ${court.court_name} สำหรับ ${customerName}`
          : `เพิ่มศาล: ${court.court_name} สำหรับ ${customerName}`
      );
    }

    setCourtModalVisible(false);
    setEditingCourt(null);
  };

  // ลบข้อมูลศาล
  const handleDeleteCourt = (customerIndex) => {
    const courtInfo = selectedCourtData[customerIndex];

    if (!courtInfo) return;

    Modal.confirm({
      title: "ยืนยันการลบ",
      content: courtInfo.applyToAll
        ? "ศาลนี้ใช้กับทุกคน คุณต้องการลบออกจากทุกคนใช่หรือไม่?"
        : "คุณต้องการลบข้อมูลศาลนี้ใช่หรือไม่?",
      okText: "ลบ",
      okType: "danger",
      cancelText: "ยกเลิก",
      onOk: () => {
        if (courtInfo.applyToAll) {
          // ลบออกจากทุก customer
          setSelectedCourtData({});
          message.success("ลบข้อมูลศาลจากทุก Customer สำเร็จ");
        } else {
          // ลบเฉพาะ customer นี้
          setSelectedCourtData((prev) => {
            const newData = { ...prev };
            delete newData[customerIndex];
            return newData;
          });
          message.success("ลบข้อมูลศาลสำเร็จ");
        }
      },
    });
  };

  // ==================== ADDRESS HANDLERS ====================
  const handleAddAddress = (person) => {
    setCurrentPerson(person);
    setEditingAddress(null);
    setAddressModalVisible(true);
  };

  const handleEditAddress = (person, address) => {
    setCurrentPerson(person);
    setEditingAddress(address);
    setAddressModalVisible(true);
  };

  // ✅ ปรับปรุง handleDeleteAddress - เช็คว่าเป็นที่อยู่เดิมหรือไม่
  const handleDeleteAddress = (personKey, addressKey, address) => {
    if (isOldAddress(address)) {
      message.warning("ไม่สามารถลบที่อยู่เดิมได้ กรุณาใช้ฟังก์ชัน 'แก้ไข' แทน");
      return;
    }

    Modal.confirm({
      title: "ยืนยันการลบ",
      content: "คุณต้องการลบที่อยู่นี้ใช่หรือไม่?",
      okText: "ลบ",
      okType: "danger",
      cancelText: "ยกเลิก",
      onOk: () => {
        setCustomerAddr((prev) =>
          prev.map((person) =>
            person.key === personKey
              ? {
                  ...person,
                  ADDRESS: person.ADDRESS.filter(
                    (addr) => addr.key !== addressKey
                  ),
                }
              : person
          )
        );
        message.success("ลบที่อยู่สำเร็จ");
      },
    });
  };

  const handleSaveAddress = (addressData) => {
    setCustomerAddr((prev) =>
      prev.map((person) => {
        if (person.key === currentPerson.key) {
          if (editingAddress) {
            return {
              ...person,
              ADDRESS: person.ADDRESS.map((addr) =>
                addr.key === editingAddress.key
                  ? { ...addr, ...addressData }
                  : addr
              ),
            };
          } else {
            const newAddress = {
              ...addressData,
              key: `addr-${Date.now()}`,
            };
            return {
              ...person,
              ADDRESS: [...person.ADDRESS, newAddress],
            };
          }
        }
        return person;
      })
    );

    message.success(
      editingAddress ? "แก้ไขที่อยู่สำเร็จ" : "เพิ่มที่อยู่สำเร็จ"
    );
    setAddressModalVisible(false);
  };

  // ✅ ฟังก์ชันยืนยันข้อมูล
  const handleConfirm = () => {
    const validationIssues = validateBeforeSubmit();

    if (validationIssues.length > 0) {
      // แสดง Modal แจ้งเตือนปัญหา
      Modal.warning({
        title: "ข้อมูลยังไม่ครบถ้วน",
        width: 600,
        content: (
          <div style={{ marginTop: 16 }}>
            {validationIssues.map((issue, idx) => (
              <Card
                key={idx}
                size="small"
                style={{ marginBottom: 12 }}
                styles={{
                  body: { padding: "12px" },
                }}
              >
                <Space direction="vertical" size={8} style={{ width: "100%" }}>
                  <Typography.Text strong style={{ fontSize: 15 }}>
                    <UserOutlined /> {issue.personName}
                  </Typography.Text>
                  {issue.issues.map((item, itemIdx) => (
                    <Tag
                      key={itemIdx}
                      icon={<WarningOutlined />}
                      color="warning"
                      style={{ fontSize: 13, padding: "4px 8px" }}
                    >
                      {item.message}
                    </Tag>
                  ))}
                </Space>
              </Card>
            ))}
            <Divider style={{ margin: "16px 0" }} />
            <Typography.Text type="secondary" style={{ fontSize: 13 }}>
              กรุณาเพิ่มข้อมูลที่ขาดให้ครบถ้วนก่อนยืนยัน
            </Typography.Text>
          </div>
        ),
        okText: "ตกลง",
      });
      return;
    }

    const finalData = prepareDataForSubmit();
    const { total: totalCourtFee, courtDetails } =
      calculateTotalCourtFee(finalData);

    console.log("=== FINAL DATA FOR SUBMISSION ===");
    console.log("Customer Addresses with Courts:", finalData);
    console.log("Total Court Fee:", totalCourtFee);
    console.log("Court Details:", courtDetails); // ✅ เช็คว่ามีข้อมูลหรือไม่

    Modal.confirm({
      title: "ข้อมูลครบถ้วน",
      width: 850,
      centered: true,
      icon: <CheckCircleOutlined style={{ color: "#52c41a" }} />,
      content: (
        <CourtFeeConfirmContent
          courtDetails={courtDetails}
          initialTotal={totalCourtFee}
          currencyFormatPoint={currencyFormatPoint}
          finalData={finalData}
          onConfirm={handleApiAddress}
        />
      ),
      okText: "ยืนยัน",
      cancelText: "ยกเลิก",
      okButtonProps: {
        size: "large",
        type: "primary",
      },
      cancelButtonProps: {
        size: "large",
      },
      footer: null,
    });
  };

  const CourtFeeConfirmContent = ({
    courtDetails,
    initialTotal,
    currencyFormatPoint,
    finalData,
    onConfirm,
  }) => {
    const [localCourtDetails, setLocalCourtDetails] = useState(
      JSON.parse(JSON.stringify(courtDetails)) // Deep clone
    );
    const [localTotal, setLocalTotal] = useState(initialTotal);

    // ✅ ฟังก์ชันอัพเดทค่าส่งหมาย
    const handleLocalUpdateFee = (courtIndex, locationIndex, newFee) => {
      const updated = [...localCourtDetails];
      updated[courtIndex].locations[locationIndex].fee =
        parseFloat(newFee) || 0;

      // คำนวณ totalFee ของศาลนั้นใหม่
      updated[courtIndex].totalFee = updated[courtIndex].locations.reduce(
        (sum, loc) => sum + (parseFloat(loc.fee) || 0),
        0
      );

      setLocalCourtDetails(updated);

      // คำนวณค่ารวมทั้งหมดใหม่
      const newTotal = updated.reduce((sum, court) => sum + court.totalFee, 0);
      setLocalTotal(newTotal);
    };

    // ✅ ฟังก์ชันอัพเดท finalData และยืนยัน
    const handleConfirmClick = () => {
      console.log("=== CONFIRMED ===");
      console.log("Total Court Fee:", localTotal); // ค่าที่แก้ไขแล้ว
      console.log("Updated Court Details:", localCourtDetails);

      // อัพเดทข้อมูล finalData ด้วยค่าส่งหมายที่แก้ไขแล้ว
      const updatedFinalData = updateFinalDataWithEditedFees(
        finalData,
        localCourtDetails
      );

      // ✅ เรียก onConfirm พร้อมส่งทั้ง updatedFinalData และ localTotal
      onConfirm(updatedFinalData, localTotal);

      Modal.destroyAll(); // ✅ ปิด Modal
    };

    const handleCancelClick = () => {
      console.log("=== CANCELLED ===");
      message.info("ยกเลิกการบันทึก");
      Modal.destroyAll(); // ✅ ปิด Modal
    };

    return (
      <div style={{ marginTop: 16 }}>
        <Space direction="vertical" size={12} style={{ width: "100%" }}>
          <Typography.Text style={{ fontSize: 15 }}>
            ✓ ค่าส่งหมายรวม:{" "}
            <span
              style={{ fontWeight: "bold", color: "#1890ff", fontSize: 16 }}
            >
              {currencyFormatPoint(localTotal)} บาท
            </span>
          </Typography.Text>

          {localCourtDetails.length > 0 ? (
            <Collapse
              items={localCourtDetails.map((court, courtIndex) => ({
                key: courtIndex,
                label: (
                  <Space
                    style={{ width: "100%", justifyContent: "space-between" }}
                  >
                    <Typography.Text strong style={{ fontSize: 14 }}>
                      {courtIndex + 1}. {court.courtName}
                    </Typography.Text>
                    <Tag color="blue" style={{ fontSize: 13 }}>
                      {court.locations.length} พื้นที่ |{" "}
                      {currencyFormatPoint(court.totalFee)} บาท
                    </Tag>
                  </Space>
                ),
                children: (
                  <Space
                    direction="vertical"
                    size={12}
                    style={{ width: "100%" }}
                  >
                    <List
                      size="small"
                      bordered
                      dataSource={court.locations}
                      renderItem={(loc, locIndex) => (
                        <List.Item>
                          <Space
                            direction="vertical"
                            size={4}
                            style={{ width: "100%" }}
                          >
                            <Typography.Text style={{ fontSize: 13 }}>
                              📍 พื้นที่ที่ {locIndex + 1}: {loc.address}, ต.
                              {loc.subDistrict}, อ.{loc.district}, จ.
                              {court.province}
                            </Typography.Text>
                            <Space align="center">
                              <Typography.Text
                                style={{
                                  fontSize: 13,
                                  color: "green",
                                  fontWeight: 500,
                                }}
                              >
                                💰 ค่าส่งหมาย:
                              </Typography.Text>
                              <InputNumber
                                value={loc.fee}
                                onChange={(value) =>
                                  handleLocalUpdateFee(
                                    courtIndex,
                                    locIndex,
                                    value
                                  )
                                }
                                formatter={(value) =>
                                  `${value}`.replace(
                                    /\B(?=(\d{3})+(?!\d))/g,
                                    ","
                                  )
                                }
                                parser={(value) =>
                                  value.replace(/\$\s?|(,*)/g, "")
                                }
                                min={0}
                                step={50}
                                style={{ width: 120 }}
                                size="small"
                                addonAfter="บาท"
                              />
                            </Space>
                          </Space>
                        </List.Item>
                      )}
                    />

                    {court.remark && (
                      <>
                        <Divider style={{ margin: "12px 0" }} />
                        <Alert
                          message="หมายเหตุ"
                          description={
                            <Typography.Text
                              style={{ fontSize: 13, whiteSpace: "pre-wrap" }}
                            >
                              {court.remark}
                            </Typography.Text>
                          }
                          type="info"
                          showIcon
                        />
                      </>
                    )}
                  </Space>
                ),
              }))}
              defaultActiveKey={[0]}
              style={{ marginTop: 12 }}
            />
          ) : (
            <Alert
              message="ไม่พบข้อมูลศาล"
              description="กรุณาตรวจสอบว่าได้เลือกศาลสำหรับทุกคนแล้ว"
              type="warning"
              showIcon
            />
          )}

          {/* ✅ ปุ่มยืนยันและยกเลิก */}
          <Space
            style={{ width: "100%", justifyContent: "flex-end", marginTop: 16 }}
          >
            <Button size="large" onClick={handleCancelClick}>
              ยกเลิก
            </Button>
            <Button
              type="primary"
              size="large"
              onClick={handleConfirmClick}
              disabled={localCourtDetails.length === 0}
            >
              ยืนยัน
            </Button>
          </Space>
        </Space>
      </div>
    );
  };

  // ✅ ฟังก์ชันอัพเดท finalData ด้วยค่าส่งหมายที่แก้ไขแล้ว
  const updateFinalDataWithEditedFees = (finalData, editableCourtDetails) => {
    return finalData.map((customer) => {
      if (!customer.COURT) return customer;

      // หาศาลที่ตรงกันใน editableCourtDetails
      const matchingCourt = editableCourtDetails.find(
        (court) => court.courtName === customer.COURT.court_name
      );

      if (!matchingCourt) return customer;

      // หาพื้นที่ที่ตรงกัน
      let address = customer.ADDRESS?.find((addr) => addr.ADDRNO === 4);
      if (!address) {
        address = customer.ADDRESS?.find((addr) => addr.ADDRNO === 1);
      }
      if (!address && customer.ADDRESS?.length > 0) {
        address = customer.ADDRESS[0];
      }

      if (!address) return customer;

      // หาค่าส่งหมายที่แก้ไขแล้ว
      const matchingLocation = matchingCourt.locations.find(
        (loc) =>
          loc.address === address.ADDR1 &&
          loc.subDistrict === address.TUMB &&
          loc.district === address.AUMPDES
      );

      if (matchingLocation) {
        // อัพเดทค่าส่งหมาย
        return {
          ...customer,
          COURT: {
            ...customer.COURT,
            amtsentnotice: matchingLocation.fee.toString(),
          },
        };
      }

      return customer;
    });
  };

  const handlePreData = (data) => {
    console.log("handlePreData - Input:", data);

    const addressesToPost = [];
    const addressesToPut = [];
    let courtName;
    data.forEach((customer) => {
      const customerId = customer.id;
      const addresses = customer.ADDRESS || [];

      if (customer.GARNO === 0) {
        courtName = customer.COURT.court_name;
      }

      // ✅ เช็คว่า customer นี้มีการเพิ่ม address ใหม่หรือไม่
      const hasNewAddress = addresses.some((addr) => !isOldAddress(addr));

      addresses.forEach((address) => {
        const isOldAddressCheck = isOldAddress(address);

        if (!isOldAddressCheck) {
          // ========== กรณีที่เป็นที่อยู่ใหม่ ==========
          const newAddress = {
            CUSTOMER_ID: customerId,
            ADDRNO: address.ADDRNO,
            ADDR1: address.ADDR1,
            ADDR2: address.ADDR2 || "",
            MOOBAN: address.MOOBAN || "",
            SOI: address.SOI || "",
            TUMB: address.TUMB,
            AUMPDES: address.AUMPDES,
            PROVDES: address.PROVDES,
            ZIP: address.ZIP,
            TELP: address.TELP || "",
            address_default:
              address.ADDRNO === 4 || address.ADDRNO === 1 ? true : false,
            delivery_cost:
              address.ADDRNO === 4 || address.ADDRNO === true
                ? parseInt(customer.COURT?.amtsentnotice || 0)
                : null,
          };

          addressesToPost.push(newAddress);
        } else {
          // ========== กรณีที่เป็นที่อยู่เดิม ==========
          let isDefault = false;
          let deliveryCost = null;

          if (hasNewAddress) {
            isDefault = false;
            deliveryCost = null;
          } else {
            const hasAddrNo4 = addresses.some(
              (addr) => isOldAddress(addr) && addr.ADDRNO === 4
            );
            if (hasAddrNo4) {
              // ถ้ามี ADDRNO 4 ให้เฉพาะ ADDRNO 4 เป็น default
              isDefault = address.ADDRNO === 4;
              deliveryCost =
                address.ADDRNO === 4
                  ? parseInt(customer.COURT?.amtsentnotice || 0)
                  : null;
            } else {
              // ถ้าไม่มี ADDRNO 4 ให้ ADDRNO 1 เป็น default
              isDefault = address.ADDRNO === 1;
              deliveryCost =
                address.ADDRNO === 1
                  ? parseInt(customer.COURT?.amtsentnotice || 0)
                  : null;
            }
          }

          const existingAddress = {
            id: address.id,
            CUSTOMER_ID: address.CUSTOMER_ID,
            ADDRNO: address.ADDRNO,
            ADDR1: address.ADDR1,
            ADDR2: address.ADDR2 || "",
            MOOBAN: address.MOOBAN || "",
            SOI: address.SOI || "",
            TUMB: address.TUMB,
            AUMPDES: address.AUMPDES,
            PROVDES: address.PROVDES,
            ZIP: address.ZIP,
            TELP: address.TELP || "",
            address_default: isDefault,
            delivery_cost: deliveryCost,
          };

          addressesToPut.push(existingAddress);
        }
      });
    });

    console.log("=== Prepared Data ===");
    console.log("Addresses to POST:", addressesToPost);
    console.log("Addresses to PUT:", addressesToPut);

    return {
      addressesToPost,
      addressesToPut,
      courtName,
    };
  };

  const handleApiAddress = async (data, totalCourtFee) => {
    setLoading(true);

    const { addressesToPost, addressesToPut, courtName } = handlePreData(data);

    // ----------------------------------------
    // 1) Dedupe POST (ลบ object ซ้ำ)
    // ----------------------------------------
    const uniquePost = Array.from(
      new Map(
        addressesToPost.map((item) => [JSON.stringify(item), item])
      ).values()
    );

    // ----------------------------------------
    // 2) Dedupe PUT + filter เฉพาะที่มีการเปลี่ยนจริง
    // ----------------------------------------
    const uniquePut = Array.from(
      new Map(
        addressesToPut.map((item) => [JSON.stringify(item), item])
      ).values()
    );

    const filteredPut = uniquePut.filter(
      (address) => address.default === true || address.delivery_cost !== null
    );

    try {
      let postSuccessCount = 0;
      let postErrorCount = 0;

      // ---------------------------
      // POST
      // ---------------------------
      if (uniquePost.length > 0) {
        const postPromises = uniquePost.map((address, i) =>
          axios
            .post(baseUrl + POST_ADD_ADDRESS, address, {
              headers: HEADERS_EXPORT,
            })
            .then(() => postSuccessCount++)
            .catch((err) => {
              postErrorCount++;
              console.error(`POST error [${i + 1}]`, err);
            })
        );

        await Promise.all(postPromises);

        if (postSuccessCount > 0)
          message.success(`เพิ่มที่อยู่ใหม่ ${postSuccessCount} รายการสำเร็จ`);
      }

      // ---------------------------
      // PUT
      // ---------------------------
      let putSuccessCount = 0;
      let putErrorCount = 0;

      if (filteredPut.length > 0) {
        const putPromises = filteredPut.map((address, i) =>
          axios
            .put(baseUrl + PUT_ADD_ADDRESS, address, {
              headers: HEADERS_EXPORT,
            })
            .then(() => putSuccessCount++)
            .catch((err) => {
              putErrorCount++;
              console.error(`PUT error [${i + 1}]`, err);
            })
        );

        await Promise.all(putPromises);

        if (putSuccessCount > 0)
          message.success(`อัพเดทที่อยู่เดิม ${putSuccessCount} รายการสำเร็จ`);
      }

      // SUMMARY
      const totalSuccess = postSuccessCount + putSuccessCount;
      const totalError = postErrorCount + putErrorCount;

      if (totalSuccess > 0 && totalError === 0) {
        message.success(`บันทึกข้อมูลสำเร็จทั้งหมด ${totalSuccess} รายการ`);
      }
    } catch (error) {
      console.error("Error saving addresses:", error);
      message.error(`เกิดข้อผิดพลาด: ${error.message}`);
    } finally {
      setLoading(false);
      funcUpdate(totalCourtFee, courtName);
      handleCancel();
    }
  };

  // ==================== TABLE COLUMNS ====================
  const makeAddressColumns = (personKey) => [
    {
      title: <span style={{ fontSize: 14 }}>ลำดับ</span>,
      key: "index",
      width: 70,
      align: "center",
      render: (_, __, index) => (
        <span style={{ fontSize: 14 }}>{index + 1}</span>
      ),
    },
    {
      title: <span style={{ fontSize: 14 }}>ประเภท</span>,
      dataIndex: "ADDRNO",
      key: "ADDRNO",
      width: 130,
      render: (_, record) => {
        const typeMap = {
          1: { text: "ทะเบียนบ้าน", color: "blue" },
          2: { text: "ส่งเอกสาร", color: "green" },
          3: { text: "ที่ทำงาน", color: "orange" },
          4: { text: "ทะเบียนราษ", color: "cyan" },
        };

        const config = typeMap[record.ADDRNO] || {
          text: record.ADDRNO || "ไม่ระบุ",
          color: "default",
        };
        return (
          <Tag color={config.color} style={{ fontSize: 13 }}>
            {config.text}
          </Tag>
        );
      },
    },
    {
      title: <span style={{ fontSize: 14 }}>ที่อยู่</span>,
      key: "fullAddress",
      render: (_, record) => {
        const parts = [
          record.ADDR1,
          record.ADDR2,
          record.TUMB || record.subdistrictName,
          record.AUMPDES || record.districtName,
          record.PROVDES || record.provinceName,
          record.ZIPCODE,
        ].filter(Boolean);
        return (
          <span style={{ fontSize: 14 }}>
            {parts.length > 0 ? parts.join(" ") : "-"}
          </span>
        );
      },
    },
    {
      title: <span style={{ fontSize: 14 }}>การจัดการ</span>,
      key: "action",
      width: 150,
      align: "center",
      render: (_, record) => (
        <Space size="small">
          {isOldAddress(record) ? (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              (ที่อยู่เดิม)
            </Typography.Text>
          ) : (
            <Button
              type="link"
              size="middle"
              icon={<EditOutlined />}
              onClick={() =>
                handleEditAddress(
                  customerAddr.find((p) => p.key === personKey),
                  record
                )
              }
              style={{ fontSize: 14 }}
            >
              แก้ไข
            </Button>
          )}
          {/* ✅ แสดงปุ่มลบเฉพาะที่อยู่ใหม่ */}
          {!isOldAddress(record) && (
            <Button
              type="link"
              danger
              size="middle"
              icon={<DeleteOutlined />}
              onClick={() => handleDeleteAddress(personKey, record.key, record)}
              style={{ fontSize: 14 }}
            >
              ลบ
            </Button>
          )}
        </Space>
      ),
    },
  ];

  // ✅ เพิ่มปุ่มแก้ไขศาล
  const makeCourtColumns = (customerIndex) => [
    {
      title: <span style={{ fontSize: 14 }}>ลำดับ</span>,
      key: "index",
      width: 80,
      align: "center",
      render: () => <span style={{ fontSize: 14 }}>1</span>,
    },
    {
      title: <span style={{ fontSize: 14 }}>ชื่อศาล</span>,
      dataIndex: "court_name",
      key: "court_name",
      render: (text) => (
        <span style={{ fontSize: 14, fontWeight: 500 }}>{text}</span>
      ),
    },
    {
      title: <span style={{ fontSize: 14 }}>ค่าส่งหมาย</span>,
      dataIndex: "amtsentnotice",
      key: "amtsentnotice",
      width: 120,
      align: "center",
      render: (text) => (
        <span style={{ fontSize: 14, fontWeight: 500, color: "#1890ff" }}>
          {currencyFormatPoint(text)} บาท
        </span>
      ),
    },
    {
      title: <span style={{ fontSize: 14 }}>หมายเหตุ</span>,
      dataIndex: "remark",
      key: "remark",
      align: "left",
      render: (text) => (
        <span
          style={{
            fontSize: 14,
            whiteSpace: "normal",
            wordBreak: "break-word",
          }}
        >
          {text || "-"}
        </span>
      ),
    },
    {
      title: <span style={{ fontSize: 14 }}>การจัดการ</span>,
      key: "action",
      width: 150,
      align: "center",
      render: () => (
        <Space size="small">
          <Button
            type="link"
            size="middle"
            icon={<EditOutlined />}
            onClick={() => handleEditCourt(customerIndex)}
            style={{ fontSize: 14 }}
          >
            แก้ไข
          </Button>
          <Button
            type="link"
            danger
            size="middle"
            icon={<DeleteOutlined />}
            onClick={() => handleDeleteCourt(customerIndex)}
            style={{ fontSize: 14 }}
          >
            ลบ
          </Button>
        </Space>
      ),
    },
  ];

  // ✅ สร้าง Tags แจ้งเตือนสำหรับแต่ละคน
  const getPersonStatusTags = (person) => {
    const tags = [];
    const courtInfo = selectedCourtData[person.customerIndex];

    // เช็คที่อยู่ทะเบียนราษ
    const hasRegistrationAddress = person.ADDRESS?.some(
      (addr) => addr.ADDRNO === 4
    );
    if (!hasRegistrationAddress) {
      tags.push(
        <Tag
          key="no-reg-address"
          icon={<WarningOutlined />}
          color="warning"
          style={{ fontSize: 12 }}
        >
          ที่อยู่เดิม
        </Tag>
      );
    } else {
      tags.push(
        <Tag
          key="has-reg-address"
          icon={<CheckCircleOutlined />}
          color="success"
          style={{ fontSize: 12 }}
        >
          มีที่อยู่ทะเบียนราษแล้ว
        </Tag>
      );
    }

    // เช็คศาล
    if (!courtInfo) {
      tags.push(
        <Tag
          key="no-court"
          icon={<WarningOutlined />}
          color="error"
          style={{ fontSize: 12 }}
        >
          ต้องเพิ่มศาล
        </Tag>
      );
    } else {
      const fee = parseFloat(courtInfo.courtData.amtsentnotice) || 0;
      const court = courtInfo.courtData.court_name || "ยังไม่เลือก";
      tags.push(
        <Tag
          key="has-court"
          icon={<CheckCircleOutlined />}
          color="success"
          style={{ fontSize: 12 }}
        >
          {court} (ค่าส่งหมาย: {currencyFormatPoint(fee)} บาท)
        </Tag>
      );
    }

    return tags;
  };

  // ==================== COLLAPSE ITEMS ====================
  const items = customerAddr.map((person) => {
    const hasAddress = person.ADDRESS && person.ADDRESS.length > 0;
    const isCustomer = person.ROLE === "CUSTOMER";
    const courtInfo = selectedCourtData[person.customerIndex];
    const hasCourtData = !!courtInfo;

    // สร้าง dataSource สำหรับตารางศาล
    const courtDataSource = hasCourtData ? [courtInfo.courtData] : [];

    return {
      key: person.key,
      collapsible: hasAddress ? null : "disabled",
      label: (
        <div
          onClick={() => hasAddress && toggleItem(person.key)}
          style={{ cursor: hasAddress ? "pointer" : "default" }}
          role="button"
        >
          <Space style={{ width: "100%", justifyContent: "space-between" }}>
            <Space direction="vertical" size={6}>
              <Tag
                color={isCustomer ? "purple" : "cyan"}
                icon={<UserOutlined />}
                style={{ fontSize: 13 }}
              >
                <strong>
                  {isCustomer ? "ผู้กู้" : `ผู้ค้ำ (${person.GARNO})`}
                </strong>
              </Tag>

              <div style={{ fontSize: 15, fontWeight: 600 }}>
                {fullName(person) || "ไม่ระบุชื่อ"}
              </div>

              <div style={{ fontSize: 13, color: "#666" }}>
                เลขบัตร: {person.CUSCOD || "ไม่ระบุ"}
              </div>

              {/* ✅ แสดง Tags สถานะ */}
              <Space wrap style={{ marginTop: 8 }}>
                {getPersonStatusTags(person)}
              </Space>
            </Space>

            <Space direction="vertical" size={10} align="end">
              <Tag
                color={hasAddress ? "green" : "orange"}
                icon={<EnvironmentOutlined />}
                style={{ fontSize: 13 }}
              >
                {hasAddress
                  ? `✓ ${person.ADDRESS.length} ที่อยู่`
                  : "✗ ไม่มีที่อยู่"}
              </Tag>

              <Button
                color="blue"
                variant="outlined"
                icon={<PlusOutlined />}
                onClick={(e) => {
                  e.stopPropagation();
                  handleAddAddress(person);
                }}
                style={{ fontSize: 14 }}
              >
                เพิ่มที่อยู่
              </Button>
            </Space>
          </Space>
        </div>
      ),
      children: (
        <>
          <Card
            title={
              <span style={{ fontSize: 15, fontWeight: 600 }}>
                รายละเอียดที่อยู่ - {fullName(person)}
              </span>
            }
            size="small"
            style={{ marginBottom: 12 }}
            styles={{
              header: {
                background: hasAddress ? "#f6ffed" : "#fff7e6",
                borderBottom: `2px solid ${hasAddress ? "#b7eb8f" : "#ffd591"}`,
                padding: "12px 16px",
              },
              body: {
                padding: "16px",
              },
            }}
          >
            {hasAddress ? (
              <Table
                dataSource={person.ADDRESS}
                columns={makeAddressColumns(person.key)}
                rowKey={(row) => row.key}
                pagination={false}
                size="middle"
                bordered
              />
            ) : (
              <Typography.Text type="secondary" style={{ fontSize: 14 }}>
                ไม่มีข้อมูลที่อยู่
              </Typography.Text>
            )}
          </Card>

          <Card
            title={
              <span style={{ fontSize: 15, fontWeight: 600 }}>ข้อมูลศาล</span>
            }
            size="small"
            extra={
              hasCourtData ? (
                <Tag
                  icon={<LockOutlined />}
                  color="orange"
                  style={{ fontSize: 13 }}
                >
                  มีศาลอยู่แล้ว (แก้ไขหรือลบได้)
                </Tag>
              ) : (
                <Button
                  color="blue"
                  variant="outlined"
                  icon={<PlusOutlined />}
                  onClick={() => handleOpenCourtModal(person.customerIndex)}
                  style={{ fontSize: 14 }}
                >
                  เพิ่มศาล
                </Button>
              )
            }
            styles={{
              header: {
                background: "#e6f7ff",
                borderBottom: "2px solid #91d5ff",
                padding: "12px 16px",
              },
              body: {
                padding: "16px",
              },
            }}
          >
            {hasCourtData && courtInfo.applyToAll && (
              <Alert
                message="ศาลนี้ถูกใช้กับทุกคนในสัญญา"
                description={`ศาลถูกสร้างโดย ${fullName(
                  customerAddr[courtInfo.sourceCustomerIndex]
                )} และนำไปใช้กับทุกคน`}
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
              />
            )}
            {hasCourtData &&
              !courtInfo.applyToAll &&
              courtInfo.sourceCustomerIndex !== person.customerIndex && (
                <Alert
                  message="ศาลนี้ถูกคัดลอกมาจาก Customer อื่น"
                  description={`ศาลต้นฉบับถูกสร้างโดย ${fullName(
                    customerAddr[courtInfo.sourceCustomerIndex]
                  )}`}
                  type="warning"
                  showIcon
                  style={{ marginBottom: 16 }}
                />
              )}
            <Table
              dataSource={courtDataSource}
              columns={makeCourtColumns(person.customerIndex)}
              rowKey={() => "court-1"}
              pagination={false}
              size="middle"
              bordered
              locale={{ emptyText: "ไม่มีข้อมูลศาล" }}
            />
          </Card>
        </>
      ),
    };
  });

  return (
    <>
      <Modal
        title={
          <Space direction="vertical" size={4}>
            <span style={{ fontSize: 18 }}>
              เลือกที่ส่งหมาย | ทุนทรัพย์ {currencyFormatPoint(courtFee)} บาท
            </span>
          </Space>
        }
        open={open}
        width={1150}
        onCancel={handleCancel}
        keyboard
        maskClosable={false}
        centered
        footer={[
          <Button key="close" danger size="large" onClick={handleCancel}>
            <span style={{ fontSize: 15 }}>ปิด</span>
          </Button>,
          <Button
            key="confirm"
            type="primary"
            size="large"
            onClick={handleConfirm}
          >
            <span style={{ fontSize: 15 }}>บันทึก</span>
          </Button>,
        ]}
        style={{ top: 20 }}
      >
        <div style={{ marginBottom: 24 }}>
          <Typography.Title
            level={5}
            style={{ fontSize: 16, marginBottom: 16 }}
          >
            รายละเอียดที่อยู่และศาล
          </Typography.Title>
          <Collapse
            items={items}
            accordion
            activeKey={activeKeys}
            onChange={setActiveKeys}
          />
        </div>
      </Modal>

      {/* Modal เพิ่ม/แก้ไข ที่อยู่ */}
      <AddressFormModal
        visible={addressModalVisible}
        onCancel={() => setAddressModalVisible(false)}
        onSave={handleSaveAddress}
        currentPerson={currentPerson}
        editingAddress={editingAddress}
        geoRawData={geoRawData}
      />

      {/* Modal เลือกศาล */}
      <CourtSearchModal
        visible={courtModalVisible}
        onCancel={() => {
          setCourtModalVisible(false);
          setEditingCourt(null);
        }}
        onSelectCourt={handleSelectCourt}
        geoRawData={geoRawData}
        searchAddress={
          currentCourtCustomerIndex !== null
            ? findSearchAddress(currentCourtCustomerIndex)
            : null
        }
        customerIndex={currentCourtCustomerIndex}
        customerName={
          currentCourtCustomerIndex !== null
            ? fullName(customerAddr[currentCourtCustomerIndex])
            : ""
        }
        allCustomers={customerAddr}
        editingCourt={editingCourt}
      />
    </>
  );
};

export default SelectCourtArea;
